/**
 * Left-nav groups for the staff app shell.
 *
 * Labels and grouping follow the live Lovable staff menu Nikki asked to match:
 * BOOKING / EMR, ORDERING, BUSINESS, then ADMIN / PROVIDER / MD OVERSIGHT.
 * Destinations that are not built yet are stub hash routes.
 */

export type NavGroupId =
  | 'booking'
  | 'ordering'
  | 'business'
  | 'admin'
  | 'provider'
  | 'oversight';

export interface StaffNavItem {
  readonly path: string;
  readonly label: string;
  readonly icon: StaffNavIcon;
  readonly stub: boolean;
}

export type StaffNavIcon =
  | 'calendar'
  | 'patients'
  | 'chart'
  | 'service'
  | 'orders'
  | 'checkout'
  | 'business'
  | 'admin'
  | 'provider'
  | 'oversight'
  | 'profile'
  | 'support';

export interface StaffNavGroup {
  readonly id: NavGroupId;
  readonly label: string;
  readonly collapsedByDefault: boolean;
  readonly items: readonly StaffNavItem[];
}

export const STAFF_NAV_GROUPS: readonly StaffNavGroup[] = [
  {
    id: 'booking',
    label: 'BOOKING / EMR',
    collapsedByDefault: false,
    items: [
      { path: '/calendar', label: 'Calendar', icon: 'calendar', stub: false },
      { path: '/patients', label: 'Patients', icon: 'patients', stub: true },
      { path: '/chart', label: 'Chart / EMR', icon: 'chart', stub: true },
    ],
  },
  {
    id: 'ordering',
    label: 'ORDERING',
    collapsedByDefault: false,
    items: [
      { path: '/service-center', label: 'Service Center', icon: 'service', stub: true },
      { path: '/orders', label: 'Sales & Order History', icon: 'orders', stub: true },
      { path: '/checkout', label: 'Checkout', icon: 'checkout', stub: true },
    ],
  },
  {
    id: 'business',
    label: 'BUSINESS',
    collapsedByDefault: false,
    items: [{ path: '/admin', label: 'Staff & locations', icon: 'business', stub: false }],
  },
  {
    id: 'admin',
    label: 'ADMIN',
    collapsedByDefault: true,
    items: [{ path: '/admin', label: 'Staff list', icon: 'admin', stub: false }],
  },
  {
    id: 'provider',
    label: 'PROVIDER',
    collapsedByDefault: true,
    items: [{ path: '/provider', label: 'Provider home', icon: 'provider', stub: true }],
  },
  {
    id: 'oversight',
    label: 'MD OVERSIGHT',
    collapsedByDefault: true,
    items: [{ path: '/md-oversight', label: 'Oversight home', icon: 'oversight', stub: true }],
  },
];

export const STAFF_UTILITY_ITEMS: readonly StaffNavItem[] = [
  { path: '/profile', label: 'My Profile', icon: 'profile', stub: true },
  { path: '/support', label: 'Support', icon: 'support', stub: true },
];

export const STAFF_APP_PATHS: readonly string[] = [
  ...STAFF_NAV_GROUPS.flatMap((group) => group.items.map((item) => item.path)),
  ...STAFF_UTILITY_ITEMS.map((item) => item.path),
];

export function isStaffAppPath(path: string): boolean {
  return STAFF_APP_PATHS.includes(path);
}

export function staffNavItemForPath(path: string): StaffNavItem | undefined {
  return (
    STAFF_NAV_GROUPS.flatMap((group) => group.items).find((item) => item.path === path) ??
    STAFF_UTILITY_ITEMS.find((item) => item.path === path)
  );
}

export function staffNavGroupIds(): readonly NavGroupId[] {
  return STAFF_NAV_GROUPS.map((group) => group.id);
}
